import React, { useEffect, useState } from "react";

import { useDrawolaT } from "../../storage/i18n";

type Props = {
  open: boolean;
  defaultName: string;
  onSave: (name: string) => Promise<void>;
  onClose: () => void;
};

export const CloudSaveDialog: React.FC<Props> = ({
  open,
  defaultName,
  onSave,
  onClose,
}) => {
  const t = useDrawolaT();
  const [name, setName] = useState(defaultName);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setName(defaultName);
      setBusy(false);
    }
  }, [open, defaultName]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      return;
    }
    setBusy(true);
    try {
      await onSave(trimmed);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="drawOla-cloud-modal"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="drawOla-cloud-modal__panel drawOla-cloud-modal__panel--narrow">
        <div className="drawOla-cloud-modal__header">
          <h3 className="drawOla-cloud-modal__title">
            {t("cloud.saveDialogTitle")}
          </h3>
        </div>
        <div className="drawOla-cloud-modal__body">
          <label className="drawOla-cloud-modal__label">
            {t("cloud.saveDialogLabel")}
            <input
              type="text"
              className="drawOla-cloud-modal__input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSave();
                }
              }}
            />
          </label>
        </div>
        <div className="drawOla-cloud-modal__actions">
          <button
            type="button"
            className="drawOla-cloud-modal__button drawOla-cloud-modal__button--secondary"
            onClick={onClose}
          >
            {t("cloud.cancel")}
          </button>
          <button
            type="button"
            className="drawOla-cloud-modal__button drawOla-cloud-modal__button--primary"
            onClick={handleSave}
            disabled={busy || !name.trim()}
          >
            {t("cloud.save")}
          </button>
        </div>
      </div>
    </div>
  );
};
